import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";

import messages from "../../../../../assets/locale/messages";
import { getCompanyPortalEventCrewSizePlans } from "../../../../../network/apis/companyPortalEvents";
import { getEventCrewSizePlans } from "../../../../../network/apis/events";
import { getPortalApiErrorMessage } from "../../../../../utils/companyPortal/eventApi";
import { showErrorMsg } from "../../../../../utils/Helpers";
import { isDraftEvent } from "../../../Event/BasicInfo/paymentStateUtils";
import CustomPlanBanner from "../../../CustomPlanBanner";
import DefineRolesAndShiftsIntro from "../DefineRolesAndShifts/DefineRolesAndShiftsIntro";
import CrewSizeOptions from "./CrewSizeOptions";
import CrewFeeSummary from "./CrewFeeSummary";
import CustomCrewSize from "./CustomCrewSize";
import HowChargesWork from "./HowChargesWork";
import {
  CREW_SIZE_SALES_EMAIL,
  EMPTY_CUSTOM_CREW_PLAN,
  EVENT_MODELS,
  calculateCustomCrewFee,
  fillTemplate,
  formatAmount,
  getCustomCrewPlanState,
  getEventBillingModel,
  getEventCrewSizeOption,
  getOperatingDays,
  getSubscriptionPlanName,
  toCrewSizeOption,
} from "./crewSizeUtils";

import "./ChooseCrewSize.scss";

/** The standard sizes stop here when the plans have not loaded yet (PROLINE-3924). */
const DEFAULT_MAX_CAPACITY = 300;

const ChooseCrewSize = ({
  initialEvent,
  eventScheduleBounds,
  requestsPortal,
  isActive,
  registerStepApi,
  crewSizeKey: selectedId,
  onCrewSizeChange,
  customCrewPlan,
  onCustomCrewPlanChange,
  canSetCustomCrewSize,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth?.login);
  const generalErrMsg = (messages[lang] ?? messages.en).shared.generalErrMsg;
  const t =
    messages[lang]?.events?.creatEvent?.crewSize ??
    messages.en.events.creatEvent.crewSize;

  const eventId = initialEvent?.id;
  const startAt = eventScheduleBounds?.eventStartAt ?? initialEvent?.event_start_at;
  const endAt = eventScheduleBounds?.eventEndAt ?? initialEvent?.event_end_at;
  const operatingDays =
    initialEvent?.operating_days ?? getOperatingDays(startAt, endAt);
  const model = getEventBillingModel(initialEvent, operatingDays);

  /** Plans priced by the backend for this event's company: the cards, then the custom plan (admin). */
  const [plans, setPlans] = useState([]);
  /** Language of the last plans request. */
  const plansLangRef = useRef(lang);
  // Read when the plans arrive, so a new selection doesn't ask for them again.
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  useEffect(() => {
    // Every step is mounted at once; the plans are asked for on this step, and again after a
    // language switch on any step, since the other steps show the selected plan's name.
    if (!eventId || !(isActive || lang !== plansLangRef.current)) return undefined;
    plansLangRef.current = lang;
    let cancelled = false;
    // The error is toasted here once, not also by the interceptor.
    const config = { handlerEnabled: false };
    const request =
      requestsPortal === "company"
        ? getCompanyPortalEventCrewSizePlans(eventId, undefined, config)
        : getEventCrewSizePlans(eventId, config);
    request
      .then((response) => {
        if (cancelled) return;
        const next = response?.data?.data ?? [];
        setPlans(next);
        // The shared form keeps the selected plan for the other steps: refresh its name and cost.
        const selected = next.find((plan) => plan.id === selectedIdRef.current);
        if (selected) onCrewSizeChange(selected);
      })
      // Show the reason the backend gives (e.g. the company has no subscription plan).
      // A refresh from another step stays silent and keeps what was loaded.
      .catch((error) => {
        if (cancelled || !isActive) return;
        setPlans([]);
        showErrorMsg(getPortalApiErrorMessage(error, generalErrMsg));
      });
    return () => {
      cancelled = true;
    };
    // The cost of each card depends on the dates and the billing period saved in Step 1.
  }, [
    eventId,
    isActive,
    requestsPortal,
    startAt,
    endAt,
    initialEvent?.payment_period,
    lang,
    generalErrMsg,
  ]);

  const options = useMemo(
    () => plans.filter((plan) => !plan.custom).map((plan) => toCrewSizeOption(plan)),
    [plans],
  );
  // Before the list loads, the plan saved on the event still identifies the selection.
  const customPlanRow =
    plans.find((plan) => plan.custom) ??
    (initialEvent?.crew_size_plan?.custom ? initialEvent.crew_size_plan : null);
  const isCustomSelected = customPlanRow != null && selectedId === customPlanRow.id;

  const selectedOption = useMemo(() => {
    if (isCustomSelected) {
      const option = toCrewSizeOption(customPlanRow, customCrewPlan);
      if (!option) return null;
      // The size and rate saved on the event show the backend's `crew_size_plan.cost`.
      // While the admin types other values, the fee is calculated live (no request per key).
      const saved = getEventCrewSizeOption(initialEvent);
      if (saved?.custom && saved.max === option.max && saved.rate === option.rate) {
        return { ...option, cost: saved.cost };
      }
      const fee = calculateCustomCrewFee({ rate: option.rate, model, operatingDays });
      return { ...option, cost: fee.amount };
    }
    return (
      options.find((option) => option.id === selectedId) ??
      (initialEvent?.crew_size_plan?.id === selectedId
        ? toCrewSizeOption(initialEvent.crew_size_plan)
        : null)
    );
  }, [
    customCrewPlan,
    customPlanRow,
    initialEvent,
    isCustomSelected,
    model,
    operatingDays,
    options,
    selectedId,
  ]);

  // Crew size is editable while draft; locked once the publishing request is submitted.
  const isLocked = !!initialEvent?.status && !isDraftEvent(initialEvent);

  const customState = getCustomCrewPlanState(customCrewPlan);
  const customIncomplete = !customState.isEmpty && !customState.isValid;
  // The company sees a custom plan set by the Super Admin, read-only.
  const isCustomReadOnly = isCustomSelected && !canSetCustomCrewSize;

  /** Backend errors of the last save, by Custom field, until that field changes. */
  const [serverErrors, setServerErrors] = useState({});

  /** Only one plan at a time: entering a custom size clears the selected tier. */
  const handleCustomChange = (patch) => {
    const nextPlan = { ...customCrewPlan, ...patch };
    const nextState = getCustomCrewPlanState(nextPlan);
    setServerErrors((errors) => {
      const next = { ...errors };
      Object.keys(patch).forEach((field) => delete next[field]);
      return next;
    });
    onCustomCrewPlanChange(nextPlan);
    if (nextState.isValid) {
      onCrewSizeChange(customPlanRow);
    } else if (!nextState.isEmpty || isCustomSelected) {
      onCrewSizeChange(null);
    }
  };

  /** ...and selecting a tier clears the Custom fields. */
  const handleSelectTier = (option) => {
    setServerErrors({});
    onCustomCrewPlanChange(EMPTY_CUSTOM_CREW_PLAN);
    onCrewSizeChange(plans.find((plan) => plan.id === option.id) ?? null);
  };

  const hasSelection = Boolean(selectedOption);

  /**
   * "Next" stays enabled: pressing it with no size marks the cards as missing.
   * Counts the attempts, so the cards shake again on each one.
   */
  const [missingAttempts, setMissingAttempts] = useState(0);
  const optionsRef = useRef(null);
  const customRef = useRef(null);
  // Partly filled Custom fields show their own errors instead.
  const isMissing = missingAttempts > 0 && !hasSelection && !customIncomplete;
  // Nothing to save once the crew size is locked, or when the company has a custom plan:
  // the backend refuses any crew size change from the company on it (PROLINE-4075).
  const skipPersist = isLocked || isCustomReadOnly;

  useEffect(() => {
    if (!registerStepApi) return undefined;
    registerStepApi({
      skipPersist,
      validateContinue: async () => {
        if (!hasSelection) {
          setMissingAttempts((attempts) => attempts + 1);
          (customIncomplete ? customRef : optionsRef).current?.scrollIntoView?.({
            behavior: "smooth",
            block: "center",
          });
        }
        return hasSelection;
      },
      // "Save As Draft" stays enabled: with the Custom fields partly filled or invalid it
      // points at their errors instead of saving the draft without them.
      validatePortalDraft: async () => {
        if (customIncomplete) {
          customRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        }
        return !customIncomplete;
      },
      countMissingFields: async () => (hasSelection ? 0 : 1),
      getSubmitIssues: () => [t.emptyTitle],
      buildPayload: async () => {
        if (skipPersist || !selectedOption) return {};
        return selectedOption.custom
          ? {
              crew_size_plan_id: selectedOption.id,
              custom_crew_size: selectedOption.max,
              custom_rate: selectedOption.rate,
            }
          : { crew_size_plan_id: selectedOption.id };
      },
      /**
       * A 422 on saving the custom plan shows under "Number of Applicants": the rate rules
       * are checked here first, so what the backend refuses is the size (e.g. the roles exceed it).
       */
      handlePersistError: (error) => {
        if (!selectedOption?.custom || error?.response?.status !== 422) return;
        setServerErrors({ applicants: getPortalApiErrorMessage(error, generalErrMsg) });
      },
    });
    return () => registerStepApi(null);
  }, [
    registerStepApi,
    hasSelection,
    customIncomplete,
    skipPersist,
    selectedOption,
    generalErrMsg,
  ]);

  const maxCapacity = options.length
    ? options[options.length - 1].max
    : DEFAULT_MAX_CAPACITY;
  // The plan of the event's company prices the sizes and is not selectable.
  const subscriptionPlan =
    initialEvent?.company?.subscription_plan ?? login?.company?.subscription_plan;
  const planName = getSubscriptionPlanName(subscriptionPlan, lang);

  return (
    <div className="crew-size-step">
      <DefineRolesAndShiftsIntro pageTitle={t.title} pageSubtitle={t.subtitle} />

      <CrewSizeOptions
        // A new key per attempt replays the cards' shake.
        key={missingAttempts}
        containerRef={optionsRef}
        t={t}
        options={options}
        model={model}
        selectedId={selectedId}
        invalid={isMissing}
        disabled={isLocked || isCustomReadOnly}
        onSelect={handleSelectTier}
      />

      {(canSetCustomCrewSize && customPlanRow) || isCustomSelected ? (
        <CustomCrewSize
          containerRef={customRef}
          t={t.custom}
          plan={customCrewPlan}
          state={customState}
          serverErrors={serverErrors}
          selected={isCustomSelected}
          extended={model === EVENT_MODELS.extended}
          disabled={isLocked || isCustomReadOnly}
          disabledHint={isCustomReadOnly ? t.custom.lockedHint : t.lockedHint}
          onChange={handleCustomChange}
        />
      ) : isLocked ? (
        <Typography component="p" className="text-body-sm crew-size-step__text">
          {t.lockedHint}
        </Typography>
      ) : (
        <CustomPlanBanner
          title={t.customPlanTitle}
          message={fillTemplate(t.contactSales, {
            max: formatAmount(maxCapacity),
            email: CREW_SIZE_SALES_EMAIL,
          })}
        />
      )}

      <CrewFeeSummary
        t={t}
        selectedOption={selectedOption}
        model={model}
        planName={planName}
        operatingDays={operatingDays}
        customIncomplete={customIncomplete}
      />

      <HowChargesWork t={t} />
    </div>
  );
};

ChooseCrewSize.propTypes = {
  initialEvent: PropTypes.object,
  eventScheduleBounds: PropTypes.shape({
    eventStartAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    eventEndAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  /** Which portal's API prices the plans. */
  requestsPortal: PropTypes.oneOf(["admin", "company"]),
  /** The wizard is on this step. */
  isActive: PropTypes.bool,
  registerStepApi: PropTypes.func,
  /** Id of the selected crew size plan; owned by the shared form. */
  crewSizeKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Called with the selected API plan, or null when the selection is cleared. */
  onCrewSizeChange: PropTypes.func,
  /** Raw Custom fields, owned by the shared form: `{ applicants, rate }`. */
  customCrewPlan: PropTypes.shape({
    applicants: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onCustomCrewPlanChange: PropTypes.func,
  /** Super Admin only: shows the editable Custom section. */
  canSetCustomCrewSize: PropTypes.bool,
};

ChooseCrewSize.defaultProps = {
  initialEvent: undefined,
  eventScheduleBounds: undefined,
  requestsPortal: "company",
  isActive: true,
  registerStepApi: undefined,
  crewSizeKey: null,
  onCrewSizeChange: () => {},
  customCrewPlan: EMPTY_CUSTOM_CREW_PLAN,
  onCustomCrewPlanChange: () => {},
  canSetCustomCrewSize: false,
};

export default ChooseCrewSize;
